/* -------------------------------------------------------------------------- */
/*                       Longitudes máximas de campos en DB                    */
/* -------------------------------------------------------------------------- */
// Refleja el CHARACTER_MAXIMUM_LENGTH de las columnas string del esquema dbo.
// Para columnas nvarchar(MAX) sin tope se define un límite arbitrario por UX.
// Si cambia el DDL en la base, actualizar aquí también.

export const UNBOUNDED_MAX_LENGTH = 4000;

/* -------------------------------------------------------------------------- */
/* Topes lógicos arbitrarios para campos numéricos (NumericalInput, latitudes,
   radios, odómetros, años, cantidades). Son límites de caracteres mostrados,
   no de valor numérico. */
export const NUMERIC_MAX_LENGTHS = {
  LATITUDE: 20,
  LONGITUDE: 20,
  RADIUS_METERS: 20,
  ODOMETER: 10,
  HOURMETER: 10,
  QUANTITY: 10,
  YEAR: 4,
  CC: 6,
  PERCENTAGE: 6,
  COUNT: 8,
  PRICE: 12,
  SPEED: 4,
  TEMPERATURE: 6,
  DURATION_MINUTES: 8
} as const;

/* -------------------------------------------------------------------------- */
/* Topes para campos UI-only: confirmaciones, valores no persistidos a una
   tabla específica, comandos al dispositivo, etc. Son límites arbitrarios
   lógicos según el contexto del campo. */
export const UI_ONLY_MAX_LENGTHS = {
  DALLAS_KEY: 30,
  CONTAINER_NUMBER: 20,
  CREDIT_CARD_NAME: 50,
  CREDIT_CARD_LAST_4: 4,
  CREDIT_CARD_AUTHORIZATION: 30,
  VOUCHER_NUMBER: 50,
  FUEL_COUPON: 50,
  DEVICE_SCRIPT: 200,
  GENERIC_COMMENTS: 300,
  GENERIC_REASON: 500,
  GENERIC_NAME: 200
} as const;

export const DB_FIELD_LENGTHS = {
  Agents: {
    AgentName: 500,
    ContactPhone: 100,
    PrivateCode: 50,
    Comments: 200
  },
  AssetAccessories: {
    AccessoryName: 100
  },
  AssetFuelTanks: {
    FuelTankName: 300
  },
  AssetSecuritySeals: {
    SecuritySealCode: 300,
    SecuritySealDescription: 300
  },
  Assets: {
    AssetIdentifier: 100,
    Comments: UNBOUNDED_MAX_LENGTH
  },
  AssetTypes: {
    AssetTypeName: 100
  },
  Brands: {
    BrandName: 50
  },
  ChatGroups: {
    LastMessage: 1500
  },
  ChatMessages: {
    MessageData: 1500
  },
  ConsoleCases: {
    CaseName: 300
  },
  Conversations: {
    GroupName: 255,
    LastMessagePreview: 500
  },
  EmailMessages: {
    Subject: 1024
  },
  GroupChatMessages: {
    MessageData: 300
  },
  HistoricalUnitMediaFiles: {
    FileName: 100
  },
  UnitMediaFiles: {
    FileName: 100,
    LocationName: 500
  },
  Contacts: {
    Email: 300
  },
  ContractTemplates: {
    TemplateName: 100,
    TemplateText: UNBOUNDED_MAX_LENGTH
  },
  Customers: {
    CustomerName: 500,
    BusinessName: 500,
    TaxId: 20,
    PrivateCode: 300,
    CustomerAddress: 500,
    Phone1: 50,
    Phone2: 50,
    Phone3: 50,
    ContactName1: 100,
    ContactName2: 100,
    ContactName3: 100,
    Comments: 2000,
    CustomerEmails: 1000,
    DistributionChannel: 100
  },
  Devices: {
    Imei: 100,
    Identifier: 50,
    SerialNumber: 50,
    MacAddress: 20,
    DevicePassword: 20,
    LotNumber: 50
  },
  Divisions: {
    DivisionName: 500,
    PrivateCode: 100
  },
  Drivers: {
    DriverName: 200,
    PrivateCode: 50,
    LicenseType: 2,
    LicenseNumber: 50,
    TokenNumber: 20,
    Comments: 50,
    IdentificationNumber: 200,
    ContactData: 2000,
    BloodType: 10,
    Experience: 10,
    PhoneNumber: 100,
    SecurityQuestion: 500,
    SecurityAnswer: 500
  },
  DriverPhones: {
    PhoneNumber: 20
  },
  DynamicAlerts: {
    Severity: 20,
    NodeId: 10,
    Message: 500,
    ResolutionNote: 1000
  },
  DynamicAlertTypes: {
    AlertTypeCode: 30,
    AlertTypeName: 100,
    Description: 500,
    DefaultSeverity: 20
  },
  DynamicEdges: {
    EdgeId: 10,
    OriginNodeId: 10,
    DestinationNodeId: 10,
    CountrySection: 30,
    CorridorId: 50,
    RoadType: 30
  },
  DynamicNodes: {
    NodeId: 10,
    NodeName: 100,
    Country: 50,
    NodeType: 30
  },
  ETAModels: {
    ModelName: 50
  },
  ETAModelRoutes: {
    ETARouteName: 100
  },
  EventDefinitions: {
    EventName: 300,
    Icon: 30,
    BackgroundColor: 15,
    FontColor: 15,
    IconColor: 15
  },
  FormQuestions: {
    QuestionName: 200
  },
  FormSections: {
    SectionTitle: 100
  },
  FuelReceipts: {
    FuelReceiptNumber: 100,
    GasStationName: 200
  },
  Geofences: {
    GeofenceName: 500,
    GeofenceAddress: 500,
    PrivateCode: 200,
    Color: 7
  },
  Groups: {
    Name: 100,
    ColorHex: 10
  },
  GroupTypes: {
    Description: 100
  },
  InsuranceAgents: {
    InsuranceAgentName: 100,
    InsuranceAgentFullName: 300,
    CountryIso: 3,
    Email: 100,
    PhoneNumber: 50,
    EmergencyPhoneNumber: 50,
    InternationalPhoneNumber: 50
  },
  InsuranceCompanies: {
    InsuranceCompanyName: 100,
    InsuranceCompanyFullName: 300,
    CountryIso: 3,
    Email: 100,
    PhoneNumber: 50,
    EmergencyPhoneNumber: 50,
    InternationalPhoneNumber: 50
  },
  Jobs: {
    JobTitle: 300,
    JobDescription: UNBOUNDED_MAX_LENGTH,
    JobAddress: 500,
    PrivateCode: 100,
    NotificationEmails: 1000,
    LastComment: UNBOUNDED_MAX_LENGTH
  },
  LocationClasses: {
    LocationClassName: 20,
    Icon: 20,
    IconName: 40,
    DefaultColor: 40
  },
  LocationImages: {
    ImageName: 100,
    ImageUrl: 300,
    ImageDescription: 500
  },
  Locations: {
    LocationName: 300,
    PrivateCode: 200,
    LocationAddress: 500,
    ContactName: 100,
    ContactPhone: 100,
    Icon: 30,
    DispatchEmails: 2000,
    NotificationEmails: 2000,
    RouteNotificationEmails: 2000,
    DistributionChannel: 100
  },
  LocationTypes: {
    Name: 50
  },
  MaintenanceLines: {
    MaintenanceLineName: 50
  },
  MaintenanceNotices: {
    Message: 2000,
    NotificationEmails: 1000,
    ReminderRecipients: 1000,
    DueRecipients: 1000
  },
  MaintenanceRanges: {
    Title: 50,
    Description: 500
  },
  MaintenanceTypes: {
    MaintenanceTypeName: 30
  },
  MeasurementUnits: {
    MeasurementUnitName: 100,
    Abbreviation: 100
  },
  MobileOperators: {
    MobileOperatorName: 100,
    APN: 50,
    APNUserName: 50,
    APNPassword: 50
  },
  Notices: {
    NoticeTitle: 100,
    NoticeMessage: 2000
  },
  NotificationFolders: {
    FolderName: 100
  },
  Offices: {
    OfficeName: 600,
    PrivateCode: 300
  },
  Optimizations: {
    OptimizationName: 100,
    Comments: 500
  },
  OptimizationPlans: {
    PlanName: 100,
    Comments: 500
  },
  OrderOptimizationBatches: {
    BatchName: 200
  },
  Orders: {
    OrderNumber: 100,
    ExternalId: 200,
    InvoiceNumber: 100,
    Description: 500
  },
  Operators: {
    OperatorName: 500,
    OperatorAddress: 500,
    OperatorPhone: 500,
    TaxNumber: 50,
    Comments: 500,
    ContactName: 200,
    ContactPhone: 200
  },
  OperatorContacts: {
    ContactName: 100,
    Email: 300,
    PhoneNumber: 50,
    JobTitle: 100,
    Identification: 100,
    Nacionality: 100,
    HomeAddress: 300
  },
  OrganizationContacts: {
    ContactName: 100,
    Email: 300,
    PhoneNumber: 50,
    JobTitle: 100,
    Identification: 100,
    Nacionality: 100,
    HomeAddress: 300
  },
  OrganizationContracts: {
    ContractNumber: 20,
    BusinessName: 200,
    TradeName: 200
  },
  OrganizationDownTimeReasons: {
    Description: 100
  },
  OrganizationForms: {
    FormName: 100,
    FormDescription: 500,
    FormUrl: 300,
    NotificationEmails: 1000
  },
  OrganizationOrderStatus: {
    Description: 200,
    StatusColor: 50
  },
  Organizations: {
    OrganizationName: 500,
    OrganizationAddress: 500,
    ContactEmail: 100,
    ContactName: 150,
    ContactPhone: 100,
    PrivateCode: 100,
    RegistrationCode: 100,
    RegistrationNotificationMails: 4000,
    TradeName: 300,
    TaxNumber: 50,
    OrderTag: 50,
    OrderVolumeUnit: 20
  },
  PartnerConfiguration: {
    PartnerName: 200,
    SMTPServer: 100,
    SMTPFromAddress: 100,
    SMTPUsername: 100,
    SMTPPassword: 100,
    WebTrackClassicURL: 300,
    WebTrackReloadedURL: 300,
    WebTrackApiURL: 300,
    WebTrackReactiveURL: 300,
    PublicApiURL: 500,
    HereMapsAppCode: 50,
    HereMapsAppId: 50,
    GoogleMapsApiKey: 100,
    BingMapsApiKey: 100,
    MapquestConsumerKey: 100,
    ImagesUrl: 100,
    ImagesPath: 100,
    CommonImagesUrl: 100,
    CommonImagesPath: 100,
    FilesUrl: 500,
    FilesPath: 500,
    OpenStreetMap: 200,
    LoginBackgroundSlideColor: 20,
    NavbarColor: 20,
    NavbarType: 20,
    FooterType: 20,
    Logo1Url: 500,
    Logo2Url: 500,
    LoginBackgroundUrl: 500,
    PrimaryColor: 20
  },
  Personas: {
    nombres: 50,
    apellidos: 50,
    identificacion: 50
  },
  PreviewFeatures: {
    FeatureTitle: 100,
    FeatureDescription: 500,
    DefaultValue: 200
  },
  Protocols: {
    ProtocolName: 100
  },
  ProtocolServerModels: {
    ProtocolServerDescription: 100,
    DnsName: 100,
    IpAddress: 100
  },
  Quotations: {
    QuotationNumber: 100,
    CustomerName: 300,
    BusinessName: 300,
    CustomerEmail: 300,
    CustomerTaxNumber: 100,
    RequisitionReason: 300,
    DenyRequisitionReason: 300,
    DeniedCustomerReason: 300
  },
  Reports: {
    ReportName: 50,
    StoredProcedure: 100
  },
  RouteComments: {
    Comment: UNBOUNDED_MAX_LENGTH
  },
  Ruta: {
    nombre: 50,
    abreviatura: 8,
    codigo_privado: 10
  },
  RutaAsignacion: {
    nombre: 50,
    abreviatura: 8,
    comentario: 200,
    boleta: 20
  },
  Routes: {
    RouteName: 100,
    Abbreviation: 30,
    PrivateCode: 100,
    SourceName: 100,
    TargetName: 100,
    NotificationEmails: 2000,
    GlobalNotificationEmails: 2000
  },
  RouteAssignments: {
    NotificationEmails: 2000,
    GlobalNotificationEmails: 2000,
    CompletedReason: 500,
    RemovalReason: 500,
    CancelReason: 500,
    VesselName: 200,
    VgmCertificateDescription: 200,
    StcDescription: 200,
    DriverExtraPhoneNumbers: 100,
    CustodianName: 200,
    CustodianPhoneNumber: 100,
    BillOfLading: 20,
    ContainerSize: 10,
    Voyage: 10,
    CustomField1: 50,
    CustomField2: 50,
    CustomField3: 50,
    CustomField4: 50,
    CustomField5: 50,
    CustomField6: 50
  },
  RouteCheckpoints: {
    LocationName: 300,
    NotificationEmails: 2000,
    TempPrivateCode: 200,
    StableId: 50,
    TrunkCheckpointId: 20
  },
  Schedules: {
    ScheduleName: 100,
    PrivateCode: 50
  },
  Sensors: {
    SensorName: 500,
    UniqueIdentifier: 50,
    SerialNumber: 50,
    SensorVersionId: 10
  },
  SensorModels: {
    ModelName: 100
  },
  ServiceClasses: {
    ServiceClassName: 300,
    Comments: 3000,
    PrivateCode: 50
  },
  SimCards: {
    IMSI: 20,
    CellularNumber: 20,
    ICCID: 30
  },
  SparepartCategories: {
    SparepartCategoryName: 100,
    SparepartCategoryIcon: 100,
    SparepartCategoryIconName: 100
  },
  SpareParts: {
    SparePartName: 100
  },
  SparePartProviders: {
    SparePartProviderName: 200,
    SparePartProviderPhone: 100,
    SparePartProviderAddress: 300,
    Notes: 500
  },
  SpecialReports: {
    ReportName: 255
  },
  SystemNotifications: {
    MessageTitle: 500,
    Message: 2000,
    RedirectUrl: 1000,
    BannerDesktopImageUrl: 500,
    BannerMobileImageUrl: 500
  },
  SpecialReportColumns: {
    DisplayName: 255,
    Expression: 2000,
    DataType: 100,
    ValueFormat: 100
  },
  Subdivisions: {
    SubdivisionName: 500,
    ContactPhone: 100,
    ContactEmail: 100,
    ContactAddress: 300,
    Comments: 300,
    IdentificationPassword: 300,
    PrivateCode: 100
  },
  Tags: {
    TagName: 50,
    TagColor: 10
  },
  Units: {
    UnitName: 300,
    Comments: 500,
    ContractNumber: 20,
    PrivateCode: 100,
    PrivateCode2: 200,
    ConcoxAppKey: 100,
    ConcoxAppSecret: 100,
    ConcoxAppUser: 100,
    ConcoxAppPassword: 100,
    ConcoxAppAccountName: 100,
    Alias: 15
  },
  UnitTypes: {
    Description: 100
  },
  UserContacts: {
    ContactName: 100,
    Email: 300,
    PhoneNumber: 50,
    JobTitle: 100,
    Identification: 100,
    Nacionality: 100,
    HomeAddress: 300
  },
  Users: {
    CompleteName: 200,
    Email: 256,
    UserName: 256,
    ImageName: 100,
    UnitsLabelContent: 100,
    CountriesForGoogleSearch: 50,
    Password: 100
  },
  VehicleTypes: {
    VehicleTypeName: 100
  },
  Vehicles: {
    LicensePlate: 20,
    VehicleName: 50,
    Chasis: 60,
    Motor: 60,
    Color: 50,
    Comments: 500,
    GadgetIdentifier: 50,
    GadgetPassword: 60,
    VIN: 30,
    InsurancePolicy: 30,
    InsuranceBroker: 50,
    InsuranceBrokerPhone: 30,
    InsuranceBrokerEmail: 30
  },
  VehicleMakes: {
    Description: 50
  },
  VehicleModels: {
    Description: 50
  },
  Workmanship: {
    Description: 100
  },
  Workplans: {
    WorkplanName: 300,
    PrivateCode: 200,
    Comments: 2000,
    SchedulePrivateCode: 300
  },
  WorkplanAssignments: {
    Ticket: 100,
    Comment: 500
  },
  WorkplanOrders: {
    OrderNumber: 100
  },
  WorkshopOrders: {
    InvoiceId: 50,
    OrderId: 50
  }
} as const;
